import { useEffect, useMemo, useState } from 'react';
import { Spin, Button, Modal, Form, Select, Input, Table, Tag, Space, Radio, Tooltip } from 'antd';
import { BellOutlined, DeleteOutlined, EyeOutlined, MenuOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getUser, updateUser } from '@/api/user';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser, getSelectedAgent, getSelectedAgentId } from '@/store/account/selector';
import {
	InfoType,
	InfoTypeCall,
	InfoTypeCrm,
	InfoTypeIntake,
	InfoTypeScenario,
	Show,
} from '@/interfaces/INotifications';
import { fetchGroups } from '@/api/fetchUserData';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { openModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';
import { InfoCircleOutlined } from '@ant-design/icons';
import { isValidEmail, isValidPhone, safeJSONParse } from '@/utils/helper';
import { v4 as uuidv4 } from 'uuid';
import Header from '@/components/Header';
import PreviewEmail from './components/PreviewEmail';
import { NotifKey } from 'src/enums/ENotifKey';
import PreviewSms from './components/PreviewSms';
import { ESubscriptionStatus, ESubscriptionTier } from 'src/enums/ESubscription';
import { EAgentType } from 'src/enums/EAgent';
import { ENotificationMethod, ENotificationType } from 'src/enums/ENotificationType';

interface SimpleNotification {
	id: string;
	type:
		| ENotificationType.CALL
		| ENotificationType.VOICEMAIL
		| ENotificationType.TEXT
		| ENotificationType.ROBOCALL
		| ENotificationType.SCENARIO
		| ENotificationType.CRM
		| ENotificationType.CALENDAR
		| ENotificationType.INTAKE;
	method: ENotificationMethod.EMAIL | ENotificationMethod.SMS;
	recipients: string[];
	enabled: boolean;
	createdAt: Date;
	customScenario?: string;
	crmGroupId?: string;
	crmGroupName?: string;
	showTranscript?: boolean;
	showCallSummary?: boolean;
	showIntakeForms?: boolean;
	showWorkflow?: boolean;
	showCallSummaryText?: boolean;
	showCallInfoLinkText?: boolean;
	scenarioId?: string;
	intakeWfScenario?: string;
}

interface NotificationSettings {
	email: boolean;
	sms: boolean;
	voicemail: boolean;
	emails: string[];
	phones: string[];
	showTranscript?: boolean;
	showCallSummary?: boolean;
	showIntakeForms?: boolean;
	showWorkflow?: boolean;
	showCallSummaryText?: boolean;
	showCallInfoLinkText?: boolean;
}

interface GroupSettings extends NotificationSettings {
	group_id: string;
	group_name: string;
}
interface IntakeNotifSettings extends NotificationSettings {
	intakeWfScenario: string;
}

type ManageNotificationsProps = {
	isNoviUser?: boolean;
};

export const ManageNotifications: React.FC<ManageNotificationsProps> = ({ isNoviUser = false }) => {
	const loggedInUser = useAppSelector(getLoggedInUser);
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const selectedAgent = useAppSelector(getSelectedAgent);
	const username = selectedAgent?.number || '';
	const dispatch = useAppDispatch();

	// Override parent container padding to make content touch edges
	useEffect(() => {
		// Find and modify the parent content component
		const contentComponent = document.querySelector('.content-component');
		if (contentComponent) {
			(contentComponent as HTMLElement).style.margin = '0';
			(contentComponent as HTMLElement).style.width = '100%';
		}

		// Also check for ant layout content
		const antContent = document.querySelector('.ant-layout-content');
		if (antContent) {
			(antContent as HTMLElement).style.padding = '0';
		}

		// Cleanup function to restore original styles when component unmounts
		return () => {
			if (contentComponent) {
				(contentComponent as HTMLElement).style.margin = '';
				(contentComponent as HTMLElement).style.width = '';
			}
			if (antContent) {
				(antContent as HTMLElement).style.padding = '';
			}
		};
	}, []);

	// State for preview modals
	const [previewModalOpen, setPreviewModalOpen] = useState(false);
	const [previewModalData, setPreviewModalData] = useState<{
		type: string;
		method: string;
		values: any;
	} | null>(null);
	const [customizationSettings, setCustomizationSettings] = useState({
		showTranscript: true,
		showCallSummary: true,
		showIntakeForms: true,
		showWorkflow: true,
		showCallSummaryText: true,
		showCallInfoLinkText: true,
	});
	const [previewEmail, setPreviewEmail] = useState(false);
	const [previewSms, setPreviewSms] = useState(false);

	const currentNoviReseller = useAppSelector(getLoggedInUser);

	// State for adding recipients
	const [addRecipientModalOpen, setAddRecipientModalOpen] = useState(false);
	const [selectedNotificationId, setSelectedNotificationId] = useState<string | null>(null);
	const [addRecipientForm] = Form.useForm();

	// State for drag and drop
	const [draggedItem] = useState<string | null>(null);

	// Simple notification creation state
	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [simpleNotifications, setSimpleNotifications] = useState<SimpleNotification[]>([]);
	const [createForm] = Form.useForm();

	// Form watching state
	const [formValues, setFormValues] = useState<{
		type?: string;
		method?: string;
		recipient?: string;
		customScenario?: string;
		crmGroupId?: string;
		intakeWfScenario?: string;
	}>({});

	const initialCallNotif = {
		email: false,
		sms: false,
		voicemail: false,
		emails: [],
		phones: [],
		showTranscript: true,
		showCallSummary: true,
		showIntakeForms: true,
		showWorkflow: true,
		showCallSummaryText: true,
		showCallInfoLinkText: true,
	};

	const initialNotif = {
		email: false,
		sms: false,
		voicemail: false,
		emails: [],
		phones: [],
	};

	const [callNotif, setCallNotif] = useState<InfoTypeCall>(initialCallNotif);
	const [textNotif, setTextNotif] = useState<InfoType>(initialNotif);
	const [calendarNotif, setCalendarNotif] = useState<InfoType>(initialNotif);
	const [scenarioNotif, setScenarioNotif] = useState<InfoTypeScenario[]>([]);
	const [voicemailNotif, setVoicemailNotif] = useState<InfoType>(initialNotif);
	const [robocallNotif, setRobocallNotif] = useState<InfoType>(initialNotif);
	const [crmNotif, setCrmNotif] = useState<InfoTypeCrm[]>([]);
	const [intakeWfScenarioNotif, setIntakeWfScenarioNotif] = useState<InfoTypeIntake[]>([]);
	const [intakeWfScenarios, setIntakeWfScenarios] = useState<string[]>([]);

	const isDisabled = isNoviUser
		? false
		: loggedInUser?.resellerClient
			? false
			: (selectedAgent?.type === EAgentType.OUTBOUND &&
					loggedInUser.outboundSubscriptionStatus === ESubscriptionStatus.CANCELLED) ||
				(selectedAgent?.type === EAgentType.INBOUND &&
					loggedInUser.inboundSubscriptionStatus === ESubscriptionStatus.CANCELLED);

	const isPhoneDisabled = isNoviUser
		? false
		: loggedInUser?.resellerClient
			? false
			: (selectedAgent?.type === EAgentType.OUTBOUND &&
					loggedInUser.outboundSubscriptionStatus === ESubscriptionStatus.CANCELLED) ||
				(selectedAgent?.type === EAgentType.INBOUND &&
					(loggedInUser.inboundSubscriptionStatus === ESubscriptionStatus.CANCELLED ||
						loggedInUser.inboundSubscriptionTier?.toLocaleLowerCase() !== ESubscriptionTier.PRO));

	const { data, isPending, refetch } = useQuery({
		queryKey: ['receptionist'],
		queryFn: () => getUser(selectedAgentId),
		enabled: !!selectedAgentId,
	});

	const { data: groups, isPending: isPendingCrm } = useQuery({
		queryKey: ['groups', username],
		queryFn: () => fetchGroups(username),
	});

	const { mutate: updateUserFn, isPending: isPendingUpdate } = useMutation({
		mutationKey: ['update-client'],
		mutationFn: updateUser,
		onSuccess: () => {
			refetch();
		},
	});

	useEffect(() => {
		const transformBackendToSimpleNotifications = () => {
			const notifications: SimpleNotification[] = [];

			const addNotification = (
				type: SimpleNotification['type'],
				method: ENotificationMethod.EMAIL | ENotificationMethod.SMS,
				recipients: string[],
				enabled: boolean,
				customData: Partial<SimpleNotification> = {},
			) => {
				if (recipients.length > 0) {
					notifications.push({
						id: `${type}_${method}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
						type,
						method,
						recipients,
						enabled,
						createdAt: new Date(),
						...customData,
					});
				}
			};

			// Transform call notifications
			const callCustomizationData = {
				showTranscript: callNotif.showTranscript ?? true,
				showCallSummary: callNotif.showCallSummary ?? true,
				showIntakeForms: callNotif.showIntakeForms ?? true,
				showWorkflow: callNotif.showWorkflow ?? true,
				showCallSummaryText: callNotif.showCallSummaryText ?? true,
				showCallInfoLinkText: callNotif.showCallInfoLinkText ?? true,
			};

			// Update the customizationSettings state with backend data
			setCustomizationSettings(callCustomizationData);

			addNotification(
				ENotificationType.CALL,
				ENotificationMethod.EMAIL,
				callNotif.emails,
				callNotif.email,
				callCustomizationData,
			);

			addNotification(
				ENotificationType.CALL,
				ENotificationMethod.SMS,
				callNotif.phones,
				callNotif.sms,
				callCustomizationData,
			);

			// Transform voicemail notifications
			addNotification(
				ENotificationType.VOICEMAIL,
				ENotificationMethod.EMAIL,
				voicemailNotif.emails,
				voicemailNotif.email,
			);
			addNotification(
				ENotificationType.VOICEMAIL,
				ENotificationMethod.SMS,
				voicemailNotif.phones,
				voicemailNotif.sms,
			);

			// Transform text notifications
			addNotification(
				ENotificationType.TEXT,
				ENotificationMethod.EMAIL,
				textNotif.emails,
				textNotif.email,
			);
			addNotification(
				ENotificationType.TEXT,
				ENotificationMethod.SMS,
				textNotif.phones,
				textNotif.sms,
			);

			// Transform robocall notifications
			addNotification(
				ENotificationType.ROBOCALL,
				ENotificationMethod.EMAIL,
				robocallNotif.emails,
				robocallNotif.email,
			);
			addNotification(
				ENotificationType.ROBOCALL,
				ENotificationMethod.SMS,
				robocallNotif.phones,
				robocallNotif.sms,
			);

			// Transform calendar notifications
			addNotification(
				ENotificationType.CALENDAR,
				ENotificationMethod.EMAIL,
				calendarNotif.emails,
				calendarNotif.email,
			);
			addNotification(
				ENotificationType.CALENDAR,
				ENotificationMethod.SMS,
				calendarNotif.phones,
				calendarNotif.sms,
			);

			// Transform scenario notifications
			scenarioNotif.forEach((scenario, index) => {
				const customData = {
					customScenario: scenario.scenario || `Scenario ${index + 1}`,
					scenarioId: `scenario_${index}`,
				};
				addNotification(
					ENotificationType.SCENARIO,
					ENotificationMethod.EMAIL,
					scenario.emails,
					scenario.email,
					customData,
				);
				addNotification(
					ENotificationType.SCENARIO,
					ENotificationMethod.SMS,
					scenario.phones,
					scenario.sms,
					customData,
				);
			});

			// Transform CRM notifications
			crmNotif.forEach((crm) => {
				const customData = {
					crmGroupId: crm.group_id,
					crmGroupName: crm.group_name,
				};
				addNotification(
					ENotificationType.CRM,
					ENotificationMethod.EMAIL,
					crm.emails,
					crm.email,
					customData,
				);
				addNotification(
					ENotificationType.CRM,
					ENotificationMethod.SMS,
					crm.phones,
					crm.sms,
					customData,
				);
			});

			// Transform Intake WF Scenario notifications
			intakeWfScenarioNotif.forEach((scenario, index) => {
				const customData = {
					intakeWfScenario: scenario.intakeWfScenario || `Scenario ${index + 1}`,
				};
				addNotification(
					ENotificationType.INTAKE,
					ENotificationMethod.EMAIL,
					scenario.emails,
					scenario.email,
					customData,
				);
				addNotification(
					ENotificationType.INTAKE,
					ENotificationMethod.SMS,
					scenario.phones,
					scenario.sms,
					customData,
				);
			});

			return notifications;
		};

		// Only transform and set when we have data
		if (data) {
			const transformedNotifications = transformBackendToSimpleNotifications();
			setSimpleNotifications(transformedNotifications);
		}
	}, [
		callNotif,
		textNotif,
		calendarNotif,
		scenarioNotif,
		voicemailNotif,
		robocallNotif,
		crmNotif,
		intakeWfScenarioNotif,
	]);

	// Simple notification creation functions
	const handleCreateNotification = (values: any) => {
		const recipients = values.recipient
			.split(',')
			.map((r: string) => r.trim())
			.filter(Boolean);

		const newNotification: SimpleNotification = {
			id: uuidv4(),
			type: values.type,
			method: values?.method,
			recipients,
			enabled: true,
			createdAt: new Date(),
			customScenario: values.customScenario,
			crmGroupId: values.crmGroupId,
			crmGroupName: values.crmGroupName,
			intakeWfScenario: values.intakeWfScenario,
		};

		setSimpleNotifications((prev) => [...prev, newNotification]);

		updateNotificationSettings({
			type: values.type,
			method: values?.method,
			recipients,
			customScenario: values.customScenario,
			crmGroupId: values.crmGroupId,
			crmGroupName: values.crmGroupName,
			intakeWfScenario: values.intakeWfScenario,
		});

		setCreateModalOpen(false);
		createForm.resetFields();
		setFormValues({});
	};

	const handleDeleteNotification = (record: SimpleNotification) => {
		setSimpleNotifications((prev) => prev.filter((n) => n.id !== record.id));

		updateNotificationSettings({
			type: record.type,
			method: record?.method,
			recipients: [],
			customScenario: record.customScenario,
			crmGroupId: record.crmGroupId,
			isdeleting: true,
			intakeWfScenario: record.intakeWfScenario,
		});
	};

	const handleModalClose = () => {
		setCreateModalOpen(false);
		createForm.resetFields();
		setFormValues({});
	};

	const handleFormValuesChange = (_: any, allValues: any) => {
		setFormValues(allValues);
	};

	const handleCustomizeNotification = (record: SimpleNotification) => {
		setPreviewModalData({
			type: record.type,
			method: record?.method,
			values: {
				customScenario: record.customScenario,
				crmGroupName: record.crmGroupName,
				recipient: record.recipients.join(', '),
				intakeWfScenario: record.intakeWfScenario,
			},
		});
		setPreviewModalOpen(true);
	};

	const handlePreviewModalClose = () => {
		setPreviewModalOpen(false);
		setPreviewModalData(null);
	};

	const handleAddRecipient = (notificationId: string) => {
		setSelectedNotificationId(notificationId);
		setAddRecipientModalOpen(true);
	};

	interface NotificationUpdate {
		type: string;
		method: string;
		recipients: string[];
		customScenario?: string;
		crmGroupId?: string;
		crmGroupName?: string;
		isdeleting?: boolean;
		intakeWfScenario?: string;
		fromRemoveParticipants?: boolean;
	}

	const updateNotificationSettings = (update: NotificationUpdate) => {
		const {
			type,
			method,
			recipients,
			customScenario,
			crmGroupId,
			intakeWfScenario,
			isdeleting,
			fromRemoveParticipants = false,
		} = update;
		const settingsMap: Record<string, { state: NotificationSettings; key: string }> = {
			call: { state: callNotif, key: 'call_notif_settings' },
			voicemail: { state: voicemailNotif, key: 'voicemail_notif_settings' },
			text: { state: textNotif, key: 'texting_platform_notif_settings' },
			robocall: { state: robocallNotif, key: 'robocall_notif_settings' },
			calendar: { state: calendarNotif, key: 'calendar_notif_settings' },
		};

		if (type === ENotificationType.SCENARIO && customScenario) {
			let newScenarios;

			if (isdeleting) {
				// Handle deletion for custom scenarios (when no recipients left)
				newScenarios = scenarioNotif
					.map((item) => {
						if (item.scenario === customScenario) {
							const updatedItem = { ...item };

							if (method === ENotificationMethod.EMAIL) {
								updatedItem.emails = [];
								updatedItem.email = false;
							} else if (method === ENotificationMethod.SMS) {
								updatedItem.phones = [];
								updatedItem.sms = false;
							}

							return updatedItem;
						}
						return item;
					})
					.filter((item) => {
						// Remove the scenario completely if both email and sms are false
						return item.scenario !== customScenario || item.email || item.sms;
					});
			} else {
				// Handle addition/update for custom scenarios
				const scenariosexist = scenarioNotif.map((item) => item.scenario);

				if (scenariosexist.includes(customScenario)) {
					// Update existing scenario - REPLACE recipients (not merge for updates)
					newScenarios = scenarioNotif.map((item) => {
						if (item.scenario === customScenario) {
							const updatedItem = { ...item };

							if (method === ENotificationMethod.EMAIL) {
								const updated = [...new Set([...updatedItem.emails, ...recipients])];
								updatedItem.emails = fromRemoveParticipants ? recipients : updated;
								updatedItem.email = fromRemoveParticipants
									? recipients.length > 0
									: updated.length > 0;
							} else if (method === ENotificationMethod.SMS) {
								const updated = [...new Set([...updatedItem.phones, ...recipients])];
								updatedItem.phones = fromRemoveParticipants ? recipients : updated;
								updatedItem.sms = fromRemoveParticipants
									? recipients.length > 0
									: updated.length > 0;
							}

							return updatedItem;
						}
						return item;
					});
				} else {
					// Add new scenario object
					const newScenarioObject = {
						scenario: customScenario,
						email: method === ENotificationMethod.EMAIL ? true : false,
						sms: method === ENotificationMethod.SMS ? true : false,
						voicemail: false,
						emails: method === ENotificationMethod.EMAIL ? recipients : [],
						phones: method === ENotificationMethod.SMS ? recipients : [],
					};

					newScenarios = [...scenarioNotif, newScenarioObject];
				}
			}

			updateUserFn({
				workflow_notif_settings: JSON.stringify(newScenarios),
				agentId: selectedAgentId,
			});
			return;
		}

		if (type === ENotificationType.CRM && crmGroupId) {
			let newCrmGroups;

			if (isdeleting) {
				// Handle deletion for CRM groups (when no recipients left)
				newCrmGroups = crmNotif
					.map((item) => {
						if (item.group_id === crmGroupId) {
							const updatedItem = { ...item };

							if (method === ENotificationMethod.EMAIL) {
								updatedItem.emails = [];
								updatedItem.email = false;
							} else if (method === ENotificationMethod.SMS) {
								updatedItem.phones = [];
								updatedItem.sms = false;
							}

							return updatedItem;
						}
						return item;
					})
					.filter((item) => {
						// Remove the CRM group completely if both email and sms are false
						return item.group_id !== crmGroupId || item.email || item.sms;
					});
			} else {
				// Handle addition/update for CRM groups
				const crmGroupsExist = crmNotif.map((item) => item.group_id);

				if (crmGroupsExist.includes(crmGroupId)) {
					// Update existing CRM group - REPLACE recipients (not merge for updates)
					newCrmGroups = crmNotif.map((item) => {
						if (item.group_id === crmGroupId) {
							const updatedItem = { ...item };

							if (method === ENotificationMethod.EMAIL) {
								const updated = [...new Set([...updatedItem.emails, ...recipients])];
								updatedItem.emails = fromRemoveParticipants ? recipients : updated;
								updatedItem.email = fromRemoveParticipants
									? recipients.length > 0
									: updated.length > 0;
							} else if (method === ENotificationMethod.SMS) {
								const updated = [...new Set([...updatedItem.phones, ...recipients])];
								updatedItem.phones = fromRemoveParticipants ? recipients : updated;
								updatedItem.sms = fromRemoveParticipants
									? recipients.length > 0
									: updated.length > 0;
							}

							return updatedItem;
						}
						return item;
					});
				} else {
					// Add new CRM group object
					const selectedGroup = availableGroups.find((g) => g.group_id === crmGroupId);
					const newCrmGroupObject = {
						group_id: crmGroupId,
						group_name: selectedGroup?.group_name || 'Unknown Group',
						email: method === ENotificationMethod.EMAIL ? true : false,
						sms: method === ENotificationMethod.SMS ? true : false,
						voicemail: false,
						emails: method === ENotificationMethod.EMAIL ? recipients : [],
						phones: method === ENotificationMethod.SMS ? recipients : [],
					};

					newCrmGroups = [...crmNotif, newCrmGroupObject];
				}
			}

			updateUserFn({
				crm_notif_settings: JSON.stringify(newCrmGroups),
				agentId: selectedAgentId,
			});
			return;
		}

		if (type === ENotificationType.INTAKE) {
			let newIntakeScenario;
			if (isdeleting) {
				// Handle deletion for Intake WF scenario (when no recipients left)
				newIntakeScenario = intakeWfScenarioNotif
					.map((item) => {
						if (item.intakeWfScenario === intakeWfScenario) {
							const updatedItem = { ...item };

							if (method === ENotificationMethod.EMAIL) {
								updatedItem.emails = [];
								updatedItem.email = false;
							} else if (method === ENotificationMethod.SMS) {
								updatedItem.phones = [];
								updatedItem.sms = false;
							}

							return updatedItem;
						}
						return item;
					})
					.filter((item) => {
						return item.intakeWfScenario !== intakeWfScenario || item.email || item.sms;
					});
			} else {
				const intakeScenarioExist = intakeWfScenarioNotif.map((item) => item.intakeWfScenario);
				if (intakeScenarioExist.includes(intakeWfScenario)) {
					newIntakeScenario = intakeWfScenarioNotif.map((item) => {
						if (item.intakeWfScenario === intakeWfScenario) {
							const updatedItem = { ...item };

							if (method === ENotificationMethod.EMAIL) {
								const updated = [...new Set([...updatedItem.emails, ...recipients])];
								updatedItem.emails = fromRemoveParticipants ? recipients : updated;
								updatedItem.email = fromRemoveParticipants
									? recipients.length > 0
									: updated.length > 0;
							} else if (method === ENotificationMethod.SMS) {
								const updated = [...new Set([...updatedItem.phones, ...recipients])];
								updatedItem.phones = fromRemoveParticipants ? recipients : updated;
								updatedItem.sms = fromRemoveParticipants
									? recipients.length > 0
									: updated.length > 0;
							}

							return updatedItem;
						}
						return item;
					});
				} else {
					const newIntakeScenarioObject = {
						intakeWfScenario,
						email: method === ENotificationMethod.EMAIL ? true : false,
						sms: method === ENotificationMethod.SMS ? true : false,
						voicemail: false,
						emails: method === ENotificationMethod.EMAIL ? recipients : [],
						phones: method === ENotificationMethod.SMS ? recipients : [],
					};

					newIntakeScenario = [...intakeWfScenarioNotif, newIntakeScenarioObject];
				}
			}

			updateUserFn({
				intake_notif_settings: JSON.stringify(newIntakeScenario),
				agentId: selectedAgentId,
			});
			return;
		}

		// Handle other notification types
		const setting = settingsMap[type];
		if (setting) {
			const { state, key } = setting;
			const updatedState = {
				...state,
				[method === ENotificationMethod.EMAIL ? 'emails' : 'phones']: recipients,
			};
			updateUserFn({
				[key]: JSON.stringify(updatedState),
				agentId: selectedAgentId,
			});
		}
	};

	const handleAddRecipientSubmit = (values: { newRecipient?: string }) => {
		if (!selectedNotificationId) {
			return;
		}

		const newRecipientsString = values.newRecipient?.trim();
		if (!newRecipientsString) {
			return;
		}

		const newRecipients = newRecipientsString
			.split(',')
			.map((recipient: string) => recipient.trim())
			.filter((recipient: string) => recipient.length > 0);

		if (newRecipients.length === 0) {
			return;
		}

		setSimpleNotifications((prev) =>
			prev.map((notification) => {
				if (notification.id === selectedNotificationId) {
					const updatedRecipients = [...notification.recipients, ...newRecipients];
					const updatedNotification = {
						...notification,
						recipients: updatedRecipients,
					};

					updateNotificationSettings({
						type: notification.type,
						method: notification?.method,
						recipients: updatedRecipients,
						customScenario: notification.customScenario,
						crmGroupId: notification.crmGroupId,
						crmGroupName: notification.crmGroupName,
						intakeWfScenario: notification.intakeWfScenario,
					});

					return updatedNotification;
				}
				return notification;
			}),
		);

		setAddRecipientModalOpen(false);
		setSelectedNotificationId(null);
		addRecipientForm.resetFields();
	};

	const handleRemoveRecipient = (notificationId: string, recipientIndex: number) => {
		setSimpleNotifications((prev) =>
			prev.map((notification) => {
				if (notification.id === notificationId) {
					const updatedRecipients = notification.recipients.filter(
						(_, index) => index !== recipientIndex,
					);
					const updatedNotification = {
						...notification,
						recipients: updatedRecipients,
					};

					// Check if we're removing the last recipient
					const isLastRecipient = updatedRecipients.length === 0;

					updateNotificationSettings({
						type: notification.type,
						method: notification?.method,
						recipients: updatedRecipients,
						customScenario: notification.customScenario,
						crmGroupId: notification.crmGroupId,
						isdeleting: isLastRecipient, // Only delete if no recipients left
						intakeWfScenario: notification.intakeWfScenario,
						fromRemoveParticipants: true,
					});

					return updatedNotification;
				}
				return notification;
			}),
		);
	};

	const handleUpdateCustomization = () => {
		const callNotifUp = {
			...callNotif,
			showTranscript: customizationSettings.showTranscript,
			showCallSummary: customizationSettings.showCallSummary,
			showIntakeForms: customizationSettings.showIntakeForms,
			showWorkflow: customizationSettings.showWorkflow,
			showCallSummaryText: customizationSettings.showCallSummaryText,
			showCallInfoLinkText: customizationSettings.showCallInfoLinkText,
		};

		updateUserFn({
			call_notif_settings: JSON.stringify(callNotifUp),
			agentId: selectedAgentId,
		});
		setPreviewModalOpen(false);
	};

	interface DragState {
		draggedId: string | null;
		targetId: string | null;
	}

	const [dragState, setDragState] = useState<DragState>({
		draggedId: null,
		targetId: null,
	});

	const handleDragStart = (e: React.DragEvent, id: string) => {
		e.dataTransfer.effectAllowed = 'move';
		e.dataTransfer.setData('text/html', id);
		setDragState((prev) => ({ ...prev, draggedId: id }));
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';
	};

	const handleDrop = (e: React.DragEvent, targetId: string) => {
		e.preventDefault();
		const { draggedId } = dragState;

		if (!draggedId || draggedId === targetId) {
			setDragState({ draggedId: null, targetId: null });
			return;
		}

		setSimpleNotifications((prev) => {
			const draggedIndex = prev.findIndex((n) => n.id === draggedId);
			const targetIndex = prev.findIndex((n) => n.id === targetId);

			if (draggedIndex === -1 || targetIndex === -1) {
				return prev;
			}

			const newNotifications = [...prev];
			const [draggedNotification] = newNotifications.splice(draggedIndex, 1);
			newNotifications.splice(targetIndex, 0, draggedNotification);

			return newNotifications;
		});

		setDragState({ draggedId: null, targetId: null });
	};

	const handleDragEnd = () => {
		setDragState({ draggedId: null, targetId: null });
	};

	const getNotificationTypeLabel = (type: string) => {
		const labels: Record<string, string> = {
			call: 'Phone Calls',
			voicemail: 'Voicemails',
			text: 'Text Messages',
			robocall: 'Spam Calls',
			custom_scenario: 'Custom Scenario',
			crm_group: 'CRM Group',
			intake: 'Intake Workflow Scenario',
		};
		return labels[type] || type;
	};

	const getNotificationTypeColor = (type: string): string => {
		const colors: Record<string, string> = {
			call: 'blue',
			voicemail: 'purple',
			text: 'green',
			robocall: 'red',
			custom_scenario: 'orange',
			crm_group: 'cyan',
			intake: 'magenta',
		};
		return colors[type] || 'default';
	};

	const getNotificationPreview = (type: string, method: string, values?: any) => {
		const previews: Record<string, Record<string, string>> = {
			call: {
				email: 'You received a call from +1 (555) 123-4567. View transcript and summary.',
				sms: '📞 New call from +1 (555) 123-4567. Tap to view details.',
			},
			voicemail: {
				email: 'New voicemail from +1 (555) 123-4567. Listen to the message.',
				sms: '📧 Voicemail from +1 (555) 123-4567. Check your dashboard.',
			},
			text: {
				email: 'New text message: "Hello, I\'d like to schedule an appointment."',
				sms: '💬 Text: "Hello, I\'d like to schedule an appointment."',
			},
			robocall: {
				email: 'Spam call blocked from +1 (555) 999-0000.',
				sms: '🚫 Spam call blocked: +1 (555) 999-0000',
			},
			custom_scenario: {
				email: `Custom scenario "${values?.customScenario || 'your scenario'}" was triggered during a call.`,
				sms: `🎯 Custom scenario "${values?.customScenario || 'your scenario'}" triggered.`,
			},
			crm_group: {
				email: `CRM update for ${values?.crmGroupName || 'selected group'}: New lead information.`,
				sms: `📊 CRM update for ${values?.crmGroupName || 'selected group'}.`,
			},
			intake: {
				email: `Intake Form scenario "${values?.intakeWfScenario || 'your scenario'}" was triggered during a call.`,
				sms: `🎯 Intake Form scenario "${values?.intakeWfScenario || 'your scenario'}" triggered.`,
			},
		};
		return previews[type]?.[method] || 'Preview not available';
	};

	const handleAddRecipientModalClose = () => {
		setAddRecipientModalOpen(false);
		setSelectedNotificationId(null);
		addRecipientForm.resetFields();
	};

	// Table columns for simple notifications
	const columns = [
		{
			title: 'Notification Type',
			dataIndex: 'type',
			key: 'type',
			render: (type: string, record: SimpleNotification) => (
				<div>
					<Tag color={getNotificationTypeColor(type)}>{getNotificationTypeLabel(type)}</Tag>
					{type === ENotificationType.SCENARIO && record.customScenario && (
						<div className="text-xs text-gray-500 mt-1">"{record.customScenario}"</div>
					)}
					{type === ENotificationType.CRM && record.crmGroupName && (
						<div className="text-xs text-gray-500 mt-1">{record.crmGroupName}</div>
					)}
					{type === ENotificationType.INTAKE && record.intakeWfScenario && (
						<div className="text-xs text-gray-500 mt-1">"{record.intakeWfScenario}"</div>
					)}
				</div>
			),
		},
		{
			title: 'Method',
			dataIndex: 'method',
			key: 'method',
			render: (method: string) => (
				<Tag color={method === ENotificationMethod.EMAIL ? 'blue' : 'orange'}>
					{method === ENotificationMethod.EMAIL ? '📧 Email' : '📱 SMS'}
				</Tag>
			),
		},
		{
			title: 'Recipients',
			dataIndex: 'recipients',
			key: 'recipients',
			render: (recipients: string[], record: SimpleNotification) => (
				<div className="space-y-2">
					<div className="flex flex-wrap gap-1">
						{recipients.map((recipient, index) => (
							<Tag
								key={index}
								closable
								onClose={() => handleRemoveRecipient(record.id, index)}
								color={record?.method === ENotificationMethod.EMAIL ? 'blue' : 'orange'}
								className="text-xs"
							>
								{recipient}
							</Tag>
						))}
					</div>
					<Button
						size="small"
						type="dashed"
						icon={<BellOutlined />}
						onClick={() => handleAddRecipient(record.id)}
						className="text-xs"
					>
						Add Recipient
					</Button>
				</div>
			),
		},
		{
			title: 'Actions',
			key: 'actions',
			render: (_: any, record: SimpleNotification) => (
				<Space>
					<Button
						size="small"
						icon={<EyeOutlined />}
						onClick={() => handleCustomizeNotification(record)}
						className="border-blue-200 text-blue-700 hover:bg-blue-50"
					>
						{record.type === ENotificationType.CALL ? 'Customize' : 'Preview'}
					</Button>
					<Button
						size="small"
						danger
						icon={<DeleteOutlined />}
						onClick={() => handleDeleteNotification(record)}
					>
						Delete
					</Button>
				</Space>
			),
		},
		{
			title: '',
			key: 'reorder',
			width: 60,
			render: (_: any, record: SimpleNotification) => (
				<div
					className={`flex items-center justify-center cursor-grab hover:cursor-grabbing ${
						dragState.draggedId === record.id ? 'opacity-50' : ''
					}`}
					draggable
					onDragStart={(e) => handleDragStart(e, record.id)}
					onDragOver={handleDragOver}
					onDrop={(e) => handleDrop(e, record.id)}
					onDragEnd={handleDragEnd}
					style={{ userSelect: 'none' }}
				>
					<MenuOutlined className="text-gray-400 hover:text-gray-600 transition-colors text-lg" />
				</div>
			),
		},
	];

	const availableGroups = useMemo(() => {
		let data: {
			group_id: string;
			group_name: string;
		}[] = [];
		if (groups?.crmGroups && groups.crmGroups.length > 0) {
			data = groups.crmGroups.map((item) => {
				return {
					group_id: item.group_id,
					group_name: item.group_name,
				};
			});
		}
		return data;
	}, [groups]);

	useEffect(() => {
		refetch();
	}, [selectedAgentId]);

	useEffect(() => {
		if (data) {
			getNotificationSettings();
		}
	}, [data, availableGroups]);

	const parseNotificationSettings = (settings: string | undefined, defaultSettings: any) => {
		if (!settings) {
			return defaultSettings;
		}
		return safeJSONParse(settings, defaultSettings);
	};

	const getDefaultGroupSettings = (group: {
		group_id: string;
		group_name: string;
	}): GroupSettings => ({
		group_id: group.group_id,
		group_name: group.group_name,
		email: false,
		sms: false,
		voicemail: false,
		emails: [],
		phones: [],
	});

	const getDefaultIntakeSettings = (intakeWfScenario: string): IntakeNotifSettings => ({
		intakeWfScenario,
		email: false,
		sms: false,
		voicemail: false,
		emails: [],
		phones: [],
	});

	const getNotificationSettings = () => {
		// CRM Group notifications
		if (availableGroups.length > 0) {
			const settingsParsed = parseNotificationSettings(data?.crm_notif_settings, []);
			const groupSettings = availableGroups.map(
				(group) =>
					settingsParsed.find((setting: GroupSettings) => setting.group_id === group.group_id) ||
					getDefaultGroupSettings(group),
			);
			setCrmNotif(groupSettings);
		}

		// Call notifications
		const callSettings = parseNotificationSettings(data?.call_notif_settings, null);
		setCallNotif(
			callSettings
				? {
						email: callSettings.call_enabled || callSettings.email,
						sms: callSettings.text_enabled || callSettings.sms,
						voicemail: callSettings.voicemail_enabled || callSettings.voicemail,
						emails: callSettings.email_recipients || callSettings.emails || [],
						phones: callSettings.text_recipients || callSettings.phones || [],
						showTranscript: callSettings.showTranscript,
						showCallSummary: callSettings.showCallSummary,
						showIntakeForms: callSettings.showIntakeForms,
						showWorkflow: callSettings.showWorkflow,
						showCallSummaryText: callSettings.showCallSummaryText,
						showCallInfoLinkText: callSettings.showCallInfoLinkText,
					}
				: initialCallNotif,
		);

		// Texting platform notifications
		const textSettings = parseNotificationSettings(data?.texting_platform_notif_settings, null);
		setTextNotif(
			textSettings
				? {
						email: textSettings.enabled || textSettings.email,
						sms: textSettings.text_enabled || textSettings.sms,
						voicemail: textSettings.voicemail_enabled || textSettings.voicemail,
						emails: textSettings.email_recipients || textSettings.emails || [],
						phones: textSettings.text_recipients || textSettings.phones || [],
					}
				: initialNotif,
		);

		// Scenario workflow notifications
		const workflowSettings = parseNotificationSettings(data?.workflow_notif_settings, []);
		const scenarioSettings = workflowSettings.map((el: any) => ({
			...el,
			email: el.email ?? true,
			sms: el.sms ?? true,
			voicemail: el.voicemail ?? true,
			emails: el.email_recipients || el.emails || [],
			phones: el.text_recipients || el.phones || [],
		}));
		setScenarioNotif(scenarioSettings);

		// Voicemail notifications
		const voicemailSettings = parseNotificationSettings(data?.voicemail_notif_settings, null);
		setVoicemailNotif(voicemailSettings || initialNotif);

		// Calendar notifications
		const calendarSettings = parseNotificationSettings(data?.calendar_notif_settings, null);
		setCalendarNotif(calendarSettings || initialNotif);

		// Robocall notifications
		const robocallSettings = parseNotificationSettings(data?.robocall_notif_settings, null);
		setRobocallNotif(robocallSettings || initialNotif);

		// Intake WF Scenario Notification
		let intake_questions_workflows = safeJSONParse(data?.intake_questions_workflows, []);
		intake_questions_workflows = intake_questions_workflows.map((item) => item?.customScenario);
		intake_questions_workflows = Array.from(new Set(intake_questions_workflows));

		setIntakeWfScenarios(intake_questions_workflows);
		if (intake_questions_workflows?.length > 0) {
			const settingsParsed = parseNotificationSettings(data?.intake_notif_settings, []);
			const intakeSettings = intake_questions_workflows.map(
				(scenario: string) =>
					settingsParsed.find(
						(setting: IntakeNotifSettings) => setting.intakeWfScenario === scenario,
					) || getDefaultIntakeSettings(scenario),
			);
			setIntakeWfScenarioNotif(intakeSettings);
		}
	};

	const getMethodColor = (method: string): string => {
		return method === ENotificationMethod.EMAIL ? 'blue' : 'orange';
	};

	const getMethodIcon = (method: string): string => {
		return method === ENotificationMethod.EMAIL ? '📧' : '📱';
	};

	const getMethodLabel = (method: string): string => {
		return method === ENotificationMethod.EMAIL
			? ENotificationMethod.EMAIL
			: ENotificationMethod.SMS;
	};

	const validateRecipients = async (_: any, value: string) => {
		if (!value) {
			return Promise.resolve();
		}

		const method = formValues?.method;
		const entries = value
			.split(',')
			.map((entry: string) => entry.trim())
			.filter(Boolean);

		if (entries.length === 0) {
			return Promise.reject(new Error('Please enter at least one valid entry'));
		}

		const validators: Record<string, (entry: string) => boolean> = {
			email: (email: string) => Boolean(isValidEmail(email)),
			sms: (phone: string) => phone.startsWith('+') && Boolean(isValidPhone(phone)),
		};

		const validator = validators[method as keyof typeof validators];
		if (!validator) {
			return Promise.resolve();
		}

		const invalidEntries = entries.filter((entry) => !validator(entry));
		if (invalidEntries.length > 0) {
			const errorMessage =
				method === ENotificationMethod.EMAIL
					? `Invalid email(s): ${invalidEntries.join(', ')}`
					: `Invalid phone number(s): ${invalidEntries
							.map((phone) =>
								!phone.startsWith('+') ? `${phone} (missing "+")` : `${phone} (invalid format)`,
							)
							.join(', ')}`;
			return Promise.reject(new Error(errorMessage));
		}

		return Promise.resolve();
	};

	const getNotificationSettingsForType = (type: string) => {
		switch (type) {
			case ENotificationType.CALL:
				return callNotif;
			case ENotificationType.VOICEMAIL:
				return voicemailNotif;
			case ENotificationType.TEXT:
				return textNotif;
			case ENotificationType.ROBOCALL:
				return robocallNotif;
			case ENotificationType.CALENDAR:
				return calendarNotif;
			case ENotificationType.SCENARIO:
				return { emails: [], phones: [] };
			case ENotificationType.CRM:
				return { emails: [], phones: [] };
			case ENotificationType.INTAKE:
				return { emails: [], phones: [] };
			default:
				return { emails: [], phones: [] };
		}
	};

	const getPreviewKey = (type?: string) => {
		switch (type) {
			case ENotificationType.CALL:
				return NotifKey.DirectCall;
			case ENotificationType.VOICEMAIL:
				return NotifKey.Voicemail;
			case ENotificationType.TEXT:
				return NotifKey.DirectSMS;
			case ENotificationType.ROBOCALL:
				return NotifKey.Robocall;
			case ENotificationType.CALENDAR:
				return NotifKey.Calendar;
			case ENotificationType.SCENARIO:
				return NotifKey.Workflow;
			case ENotificationType.CRM:
				return NotifKey.CRM;
			case ENotificationType.INTAKE:
				return NotifKey.Intake;
			default:
				return NotifKey.DirectCall;
		}
	};

	const getCurrentNotificationType = () => {
		return formValues?.type || createForm.getFieldValue('type') || ENotificationType.CALL;
	};

	const isMethodDisabledForType = (method: string, type?: string) => {
		const currentType = type || getCurrentNotificationType();

		if (
			!currentType ||
			currentType === ENotificationType.SCENARIO ||
			currentType === ENotificationType.CRM ||
			currentType === ENotificationType.INTAKE
		) {
			return false;
		}

		const settings = getNotificationSettingsForType(currentType);
		if (method === ENotificationMethod.EMAIL) {
			return settings.emails && settings.emails.length > 0;
		} else if (method === ENotificationMethod.SMS) {
			return settings.phones && settings.phones.length > 0;
		}

		return false;
	};

	useEffect(() => {
		if (
			formValues?.type &&
			formValues?.method &&
			isMethodDisabledForType(formValues?.method, formValues?.type)
		) {
			createForm.setFieldsValue({ method: undefined });
			setFormValues((prev) => ({ ...prev, method: undefined }));
		}
	}, [formValues?.type, formValues?.method]);

	const handleCreateModalOpen = () => {
		setCreateModalOpen(true);
		createForm.setFieldsValue({ type: ENotificationType.CALL });
		setFormValues((prev) => ({ ...prev, type: ENotificationType.CALL }));
	};

	const PreviewShowObject = {
		transcript: callNotif.showTranscript,
		callSummary: callNotif.showCallSummary,
		intakeForms: callNotif.showIntakeForms,
		workflow: callNotif.showWorkflow,
		callSummaryText: callNotif.showCallSummaryText,
		callInformationLink: callNotif.showCallInfoLinkText,
	};

	const onPreviewShowChange = (val: Show) => {
		const props = {
			...callNotif,
			showTranscript: val.transcript,
			showCallSummary: val.callSummary,
			showIntakeForms: val.intakeForms,
			showWorkflow: val.workflow,
			showCallSummaryText: val.callSummaryText,
			showCallInfoLinkText: val.callInformationLink,
		};
		updateUserFn({
			call_notif_settings: JSON.stringify(props),
			agentId: selectedAgentId,
		});
	};

	return (
		<Spin spinning={isPending || isPendingUpdate}>
			<div className="flex flex-col gap-6">
				<Header
					icon={<BellOutlined />}
					title="Notify Me"
					description="Get notified when customers interact with your AI receptionist"
					debrand={loggedInUser.resellerClient}
				/>

				{/* Simple Notification Creation Section */}
				<div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-100 mx-2 md:mx-6">
					<div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
						<div>
							<p className="text-gray-600 text-base md:text-lg">
								Get notified anytime your someone interacts with your receptionist
							</p>
						</div>
						<Button
							type="primary"
							size="large"
							icon={<BellOutlined />}
							onClick={handleCreateModalOpen}
							className="relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white font-semibold h-12 px-4 md:px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 w-full md:w-auto"
						>
							<div className="absolute inset-0 pointer-events-none">
								<div className="absolute top-[15%] left-[15%] w-[50%] h-[50%] bg-white/20 rounded-full blur-xl" />
								<div className="absolute bottom-[15%] right-[15%] w-[50%] h-[50%] bg-white/15 rounded-full blur-xl" />
								<div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[50%] h-[50%] bg-white/10 rounded-full blur-xl" />
							</div>
							<span className="relative z-10">Create Notification</span>
						</Button>
					</div>

					{simpleNotifications.length > 0 ? (
						<div className="overflow-x-auto">
							<Table
								dataSource={simpleNotifications}
								columns={columns}
								rowKey="id"
								pagination={false}
								size="middle"
								scroll={{ x: 'max-content' }}
								onRow={(record) => ({
									onDragOver: handleDragOver,
									onDrop: (e) => handleDrop(e, record.id),
									className: `transition-all duration-200 ${
										draggedItem === record.id
											? 'bg-blue-50'
											: draggedItem && draggedItem !== record.id
												? 'border-2 border-dashed border-gray-200'
												: ''
									}`,
								})}
							/>
						</div>
					) : (
						<div className="text-center py-8 md:py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
							<div className="text-gray-400 text-lg mb-2">🔔</div>
							<p className="text-gray-500 text-base md:text-lg font-medium mb-2">
								No notifications created yet
							</p>
							<Button
								type="primary"
								size="large"
								icon={<BellOutlined />}
								onClick={() => setCreateModalOpen(true)}
								className="relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white font-semibold h-12 px-4 md:px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 w-full md:w-auto"
							>
								<div className="absolute inset-0 pointer-events-none">
									<div className="absolute top-[15%] left-[15%] w-[50%] h-[50%] bg-white/20 rounded-full blur-xl" />
									<div className="absolute bottom-[15%] right-[15%] w-[50%] h-[50%] bg-white/15 rounded-full blur-xl" />
									<div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[50%] h-[50%] bg-white/10 rounded-full blur-xl" />
								</div>
								<span className="relative z-10">Create Your First Notification</span>
							</Button>
						</div>
					)}
				</div>

				{/* Create Notification Modal */}
				<Modal
					title={
						<div className="flex items-center gap-3">
							<div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
								<BellOutlined className="text-blue-600" />
							</div>
							<span className="text-lg md:text-xl font-semibold">Create New Notification</span>
						</div>
					}
					open={createModalOpen}
					onCancel={handleModalClose}
					footer={null}
					width={window.innerWidth < 768 ? '95%' : 600}
					centered
					className="notification-modal"
				>
					<Form
						form={createForm}
						layout="vertical"
						onFinish={handleCreateNotification}
						onValuesChange={handleFormValuesChange}
						className="mt-4 md:mt-6"
					>
						<div className="space-y-4 md:space-y-6">
							{/* Step 1: What to be notified of */}
							<div className="bg-blue-50 p-3 md:p-4 rounded-lg border border-blue-100">
								<h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3">
									1. What do you want to be notified of?
								</h3>
								<Form.Item
									name="type"
									initialValue="call"
									rules={[{ required: true, message: 'Please select a notification type' }]}
								>
									<Radio.Group size="large" className="w-full">
										<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3">
											<Tooltip title="When someone calls my receptionist" placement="top">
												<Radio.Button
													value="call"
													className="!h-auto p-0 border-0 overflow-hidden"
													disabled={callNotif.emails.length > 0 && callNotif.phones.length > 0}
												>
													<div
														className={`bg-white border-2 rounded-lg p-4 hover:shadow-sm transition-all duration-200 group h-16 flex items-center ${
															formValues?.type === ENotificationType.CALL
																? 'border-blue-500 bg-blue-50 shadow-md'
																: callNotif.emails.length > 0 && callNotif.phones.length > 0
																	? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
																	: 'border-gray-200 hover:border-blue-400 cursor-pointer'
														}`}
													>
														<div className="flex items-center gap-3 w-full">
															<div
																className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
																	formValues?.type === ENotificationType.CALL
																		? 'bg-blue-200'
																		: callNotif.emails.length > 0 && callNotif.phones.length > 0
																			? 'bg-gray-200'
																			: 'bg-blue-100 group-hover:bg-blue-200'
																}`}
															>
																<span className="text-xl">📞</span>
															</div>
															<div className="flex-1 text-left min-w-0">
																<div className="text-sm font-semibold text-gray-900">
																	Phone Calls
																</div>
															</div>
														</div>
													</div>
												</Radio.Button>
											</Tooltip>

											<Tooltip title="When someone leaves a voicemail" placement="top">
												<Radio.Button
													value="voicemail"
													className="!h-auto p-0 border-0 overflow-hidden"
													disabled={
														voicemailNotif.emails.length > 0 && voicemailNotif.phones.length > 0
													}
												>
													<div
														className={`bg-white border-2 rounded-lg p-4 hover:shadow-sm transition-all duration-200 group h-16 flex items-center ${
															formValues?.type === ENotificationType.VOICEMAIL
																? 'border-purple-500 bg-purple-50 shadow-md'
																: voicemailNotif.emails.length > 0 &&
																	  voicemailNotif.phones.length > 0
																	? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
																	: 'border-gray-200 hover:border-blue-400 cursor-pointer'
														}`}
													>
														<div className="flex items-center gap-3 w-full">
															<div
																className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
																	formValues?.type === ENotificationType.VOICEMAIL
																		? 'bg-purple-200'
																		: voicemailNotif.emails.length > 0 &&
																			  voicemailNotif.phones.length > 0
																			? 'bg-gray-200'
																			: 'bg-purple-100 group-hover:bg-purple-200'
																}`}
															>
																<span className="text-xl">📧</span>
															</div>
															<div className="flex-1 text-left min-w-0">
																<div className="text-sm font-semibold text-gray-900">
																	Voicemails
																</div>
															</div>
														</div>
													</div>
												</Radio.Button>
											</Tooltip>

											<Tooltip title="When someone texts my receptionist" placement="top">
												<Radio.Button
													value="text"
													className="!h-auto p-0 border-0 overflow-hidden"
													disabled={textNotif.emails.length > 0 && textNotif.phones.length > 0}
												>
													<div
														className={`bg-white border-2 rounded-lg p-4 hover:shadow-sm transition-all duration-200 group h-16 flex items-center ${
															formValues?.type === ENotificationType.TEXT
																? 'border-green-500 bg-green-50 shadow-md'
																: textNotif.emails.length > 0 && textNotif.phones.length > 0
																	? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
																	: 'border-gray-200 hover:border-blue-400 cursor-pointer'
														}`}
													>
														<div className="flex items-center gap-3 w-full">
															<div
																className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
																	formValues?.type === ENotificationType.TEXT
																		? 'bg-green-200'
																		: textNotif.emails.length > 0 && textNotif.phones.length > 0
																			? 'bg-gray-200'
																			: 'bg-green-100 group-hover:bg-green-200'
																}`}
															>
																<span className="text-xl">💬</span>
															</div>
															<div className="flex-1 text-left min-w-0">
																<div className="text-sm font-semibold text-gray-900">
																	Text Messages
																</div>
															</div>
														</div>
													</div>
												</Radio.Button>
											</Tooltip>

											<Tooltip title="When my receptionist blocks a spam call" placement="top">
												<Radio.Button
													value="robocall"
													className="!h-auto p-0 border-0 overflow-hidden"
													disabled={
														robocallNotif.emails.length > 0 && robocallNotif.phones.length > 0
													}
												>
													<div
														className={`bg-white border-2 rounded-lg p-4 hover:shadow-sm transition-all duration-200 group h-16 flex items-center ${
															formValues?.type === ENotificationType.ROBOCALL
																? 'border-red-500 bg-red-50 shadow-md'
																: robocallNotif.emails.length > 0 && robocallNotif.phones.length > 0
																	? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
																	: 'border-gray-200 hover:border-blue-400 cursor-pointer'
														}`}
													>
														<div className="flex items-center gap-3 w-full">
															<div
																className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
																	formValues?.type === ENotificationType.ROBOCALL
																		? 'bg-red-200'
																		: robocallNotif.emails.length > 0 &&
																			  robocallNotif.phones.length > 0
																			? 'bg-gray-200'
																			: 'bg-red-100 group-hover:bg-red-200'
																}`}
															>
																<span className="text-xl">🚫</span>
															</div>
															<div className="flex-1 text-left min-w-0">
																<div className="text-sm font-semibold text-gray-900">
																	Spam Calls
																</div>
															</div>
														</div>
													</div>
												</Radio.Button>
											</Tooltip>

											<Tooltip title="When a specific scenario is triggered" placement="top">
												<Radio.Button
													value="custom_scenario"
													className="!h-auto p-0 border-0 overflow-hidden"
												>
													<div
														className={`bg-white border-2 rounded-lg p-4 hover:shadow-sm transition-all duration-200 cursor-pointer group h-16 flex items-center ${
															formValues?.type === ENotificationType.SCENARIO
																? 'border-orange-500 bg-orange-50 shadow-md'
																: 'border-gray-200 hover:border-blue-400'
														}`}
													>
														<div className="flex items-center gap-3 w-full">
															<div
																className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
																	formValues?.type === ENotificationType.SCENARIO
																		? 'bg-orange-200'
																		: 'bg-orange-100 group-hover:bg-orange-200'
																}`}
															>
																<span className="text-xl">🎯</span>
															</div>
															<div className="flex-1 text-left min-w-0">
																<div className="text-sm font-semibold text-gray-900">
																	Custom Scenario
																</div>
															</div>
														</div>
													</div>
												</Radio.Button>
											</Tooltip>

											<Tooltip title="When a customer enters a certain group" placement="top">
												<Radio.Button
													value="crm_group"
													className="!h-auto p-0 border-0 overflow-hidden"
												>
													<div
														className={`bg-white border-2 rounded-lg p-4 hover:shadow-sm transition-all duration-200 cursor-pointer group h-16 flex items-center ${
															formValues?.type === ENotificationType.CRM
																? 'border-cyan-500 bg-cyan-50 shadow-md'
																: 'border-gray-200 hover:border-blue-400'
														}`}
													>
														<div className="flex items-center gap-3 w-full">
															<div
																className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
																	formValues?.type === ENotificationType.CRM
																		? 'bg-cyan-200'
																		: 'bg-cyan-100 group-hover:bg-cyan-200'
																}`}
															>
																<span className="text-xl">📊</span>
															</div>
															<div className="flex-1 text-left min-w-0">
																<div className="text-sm font-semibold text-gray-900">CRM Group</div>
															</div>
														</div>
													</div>
												</Radio.Button>
											</Tooltip>

											<Tooltip
												title="When a specific Intake Workflow scenario is triggered"
												placement="top"
											>
												<Radio.Button
													value="intake"
													className="!h-auto p-0 border-0 overflow-hidden"
												>
													<div
														className={`bg-white border-2 rounded-lg p-4 hover:shadow-sm transition-all duration-200 cursor-pointer group h-16 flex items-center ${
															formValues?.type === ENotificationType.INTAKE
																? 'border-orange-500 bg-orange-50 shadow-md'
																: 'border-gray-200 hover:border-blue-400'
														}`}
													>
														<div className="flex items-center gap-3 w-full">
															<div
																className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
																	formValues?.type === ENotificationType.INTAKE
																		? 'bg-purple-200'
																		: 'bg-purple-100 group-hover:bg-orange-200'
																}`}
															>
																<span className="text-xl">📋</span>
															</div>
															<div className="flex-1 text-left min-w-0">
																<div className="text-sm font-semibold text-gray-900">
																	Intake Scenario
																</div>
															</div>
														</div>
													</div>
												</Radio.Button>
											</Tooltip>
										</div>
									</Radio.Group>
								</Form.Item>

								{/* Conditional field for custom scenario */}
								{formValues?.type === ENotificationType.SCENARIO && (
									<Form.Item
										name="customScenario"
										rules={[{ required: true, message: 'Please define your custom scenario' }]}
									>
										<Input.TextArea
											placeholder="Describe the scenario that should trigger this notification..."
											rows={3}
											size="large"
											maxLength={500}
											showCount
										/>
									</Form.Item>
								)}

								{/* Conditional field for CRM group */}
								{formValues?.type === ENotificationType.CRM && (
									<Form.Item
										name="crmGroupId"
										rules={[{ required: true, message: 'Please select a CRM group' }]}
									>
										{availableGroups.length > 0 ? (
											<Select
												placeholder="Select CRM group..."
												size="large"
												className="w-full"
												loading={isPendingCrm}
												onChange={(value) => {
													const selectedGroup = availableGroups.find((g) => g.group_id === value);
													if (selectedGroup) {
														createForm.setFieldsValue({ crmGroupName: selectedGroup.group_name });
													}
												}}
											>
												{availableGroups.map((group) => (
													<Select.Option key={group.group_id} value={group.group_id}>
														<div className="flex items-center gap-2">
															<span>🏢</span>
															<span>{group.group_name}</span>
														</div>
													</Select.Option>
												))}
											</Select>
										) : (
											<div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center">
												<div className="text-gray-400 text-lg mb-2">🏢</div>
												<p className="text-gray-500 font-medium mb-2">No CRM Groups Found</p>
												<p className="text-gray-400 text-sm">
													You need to set up CRM integration first to use this feature.
												</p>
											</div>
										)}
									</Form.Item>
								)}

								{/* Conditional field for Intake WF Scenario */}
								{formValues?.type === ENotificationType.INTAKE && (
									<Form.Item
										name="intakeWfScenario"
										rules={[
											{ required: true, message: 'Please select an Intake Workflow Scenario' },
										]}
									>
										{intakeWfScenarios.length > 0 ? (
											<Select
												placeholder="Select Intake Workflow Scenario..."
												size="large"
												className="w-full"
												// loading={isPendingIntake}
												onChange={(value) => {
													createForm.setFieldsValue({ intakeWfScenarios: value });
												}}
											>
												{intakeWfScenarios.map((item, index) => (
													<Select.Option key={index} value={item}>
														<div className="flex items-center gap-2">
															<span>🏢</span>
															<span>{item}</span>
														</div>
													</Select.Option>
												))}
											</Select>
										) : (
											<div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center">
												<div className="text-gray-400 text-lg mb-2">🏢</div>
												<p className="text-gray-500 font-medium mb-2">
													No Intake Workflow scenario Found
												</p>
												<p className="text-gray-400 text-sm">
													You need to add an Intake Workflow Scenario first to use this feature.
												</p>
											</div>
										)}
									</Form.Item>
								)}

								{/* Hidden field to store CRM group name */}
								<Form.Item name="crmGroupName" hidden>
									<Input />
								</Form.Item>
							</div>

							{/* Step 2: How to receive notification */}
							<div className="bg-amber-50 p-3 md:p-4 rounded-lg border border-amber-100">
								<h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3">
									2. How do you want to receive the notification?
								</h3>
								<Form.Item
									name="method"
									rules={[{ required: true, message: 'Please select a notification method' }]}
								>
									<Radio.Group size="large" className="w-full">
										<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
											<Tooltip
												title={
													isMethodDisabledForType(ENotificationMethod.EMAIL)
														? 'Email notification already exists for this type'
														: 'Detailed notifications with full content'
												}
												placement="top"
											>
												<Radio.Button
													value="email"
													className="!h-auto !p-0 !border-0 overflow-hidden"
													style={{ height: 'auto', border: 'none' }}
													disabled={isMethodDisabledForType(ENotificationMethod.EMAIL)}
												>
													<div
														className={`bg-white border-2 rounded-lg p-3 hover:shadow-sm transition-all duration-200 group h-16 flex items-center ${
															formValues?.method === ENotificationMethod.EMAIL
																? 'border-blue-500 bg-blue-50 shadow-md'
																: isMethodDisabledForType(ENotificationMethod.EMAIL)
																	? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
																	: 'border-gray-200 hover:border-blue-400 cursor-pointer'
														}`}
													>
														<div className="flex items-center gap-3 w-full">
															<div
																className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
																	formValues?.method === ENotificationMethod.EMAIL
																		? 'bg-blue-200'
																		: isMethodDisabledForType(ENotificationMethod.EMAIL)
																			? 'bg-gray-200'
																			: 'bg-blue-100 group-hover:bg-blue-200'
																}`}
															>
																<span className="text-xl">📧</span>
															</div>
															<div className="flex-1 text-left min-w-0">
																<div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
																	Email
																	{isMethodDisabledForType(ENotificationMethod.EMAIL) && (
																		<span className="text-xs text-gray-500">
																			(Already configured)
																		</span>
																	)}
																</div>
															</div>
														</div>
													</div>
												</Radio.Button>
											</Tooltip>

											<Tooltip
												title={
													isMethodDisabledForType(ENotificationMethod.SMS)
														? 'SMS notification already exists for this type'
														: 'Quick text alerts to your phone'
												}
												placement="top"
											>
												<Radio.Button
													value="sms"
													className="!h-auto !p-0 !border-0 overflow-hidden"
													style={{ height: 'auto', border: 'none' }}
													disabled={isMethodDisabledForType(ENotificationMethod.SMS)}
												>
													<div
														className={`bg-white border-2 rounded-lg p-3 hover:shadow-sm transition-all duration-200 group h-16 flex items-center ${
															formValues?.method === ENotificationMethod.SMS
																? 'border-orange-500 bg-orange-50 shadow-md'
																: isMethodDisabledForType(ENotificationMethod.SMS)
																	? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
																	: 'border-gray-200 hover:border-blue-400 cursor-pointer'
														}`}
													>
														<div className="flex items-center gap-3 w-full">
															<div
																className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
																	formValues?.method === ENotificationMethod.SMS
																		? 'bg-orange-200'
																		: isMethodDisabledForType(ENotificationMethod.SMS)
																			? 'bg-gray-200'
																			: 'bg-orange-100 group-hover:bg-orange-200'
																}`}
															>
																<span className="text-xl">📱</span>
															</div>
															<div className="flex-1 text-left min-w-0">
																<div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
																	SMS
																	{isMethodDisabledForType(ENotificationMethod.SMS) && (
																		<span className="text-xs text-gray-500">
																			(Already configured)
																		</span>
																	)}
																</div>
															</div>
														</div>
													</div>
												</Radio.Button>
											</Tooltip>
										</div>
									</Radio.Group>
								</Form.Item>

								{/* Warning message when both methods are already taken */}
								{formValues?.type &&
									formValues?.type !== ENotificationType.SCENARIO &&
									formValues?.type !== ENotificationType.CRM &&
									formValues?.type !== ENotificationType.INTAKE &&
									isMethodDisabledForType(ENotificationMethod.EMAIL, formValues?.type) &&
									isMethodDisabledForType(ENotificationMethod.SMS, formValues?.type) && (
										<div className="bg-amber-50 p-3 rounded-lg border border-amber-200 mb-3">
											<div className="flex items-center gap-2">
												<span className="text-amber-600">⚠️</span>
												<span className="text-sm text-amber-700 font-medium">
													Both Email and SMS notifications already exist for{' '}
													{getNotificationTypeLabel(formValues?.type)}
												</span>
											</div>
										</div>
									)}

								{/* SMS Credits Info - Only show when SMS is selected */}
								{formValues?.method === ENotificationMethod.SMS &&
									!loggedInUser?.resellerClient &&
									selectedAgent?.type !== EAgentType.OUTBOUND && (
										<div className="bg-blue-50 p-2 md:p-3 rounded-lg border border-blue-100 mt-2 md:mt-3 mb-3 md:mb-4">
											<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
												<div className="flex items-center gap-2">
													<InfoCircleOutlined className="text-blue-600" />
													<span className="text-sm font-medium text-blue-700">
														SMS notifications use credits
													</span>
												</div>
												<button
													className="text-blue-600 text-sm hover:underline"
													type="button"
													onClick={(e) => {
														e.preventDefault();
														dispatch(openModal({ modalId: MODAL_IDS.SMS_PRICING }));
													}}
												>
													View pricing
												</button>
											</div>
										</div>
									)}

								<Form.Item
									name="recipient"
									rules={[
										{ required: true, message: 'Please enter recipient details' },
										{ validator: validateRecipients },
									]}
									validateTrigger={['onChange', 'onBlur']}
								>
									<Input
										size="large"
										placeholder={
											formValues?.method === ENotificationMethod.EMAIL
												? 'Enter email address(es) - separate multiple with commas'
												: 'Enter phone number(s) with + prefix - separate multiple with commas'
										}
									/>
								</Form.Item>
							</div>

							{/* Step 3: Preview */}
							{formValues?.type && formValues?.method && (
								<div className="bg-green-50 p-3 md:p-4 rounded-lg border border-green-100">
									<h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3">
										3. Preview
									</h3>
									<div className="bg-white p-3 md:p-4 rounded-lg border border-green-200">
										<div className="flex items-center justify-between">
											<div className="text-sm text-gray-500 mb-2">
												Sample {formValues?.method} notification:
											</div>
											<Button
												onClick={() => {
													previewModalData?.method === ENotificationMethod.EMAIL
														? setPreviewEmail(true)
														: setPreviewSms(true);
												}}
											>
												Full Preview
											</Button>
										</div>

										<div className="text-gray-800 italic text-sm md:text-base">
											"{getNotificationPreview(formValues?.type, formValues?.method, formValues)}"
										</div>
									</div>
								</div>
							)}
						</div>

						<div className="flex flex-col sm:flex-row justify-end gap-3 mt-4 pt-3">
							<Button onClick={handleModalClose} className="w-full sm:w-auto">
								Cancel
							</Button>
							<Button
								type="primary"
								htmlType="submit"
								icon={<BellOutlined />}
								className="relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white font-semibold h-10 px-5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 w-full sm:w-auto"
							>
								<div className="absolute inset-0 pointer-events-none">
									<div className="absolute top-[15%] left-[15%] w-[50%] h-[50%] bg-white/20 rounded-full blur-xl" />
									<div className="absolute bottom-[15%] right-[15%] w-[50%] h-[50%] bg-white/15 rounded-full blur-xl" />
									<div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[50%] h-[50%] bg-white/10 rounded-full blur-xl" />
								</div>
								<span className="relative z-10">Create Notification</span>
							</Button>
						</div>
					</Form>
				</Modal>

				{/* Preview/Customize Modal */}
				<Modal
					title={
						<div className="flex items-center gap-3">
							<div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
								<EyeOutlined className="text-blue-600" />
							</div>
							<span className="text-lg md:text-xl font-semibold">
								{previewModalData?.type === ENotificationType.CALL ? 'Customize' : 'Preview'}{' '}
								Notification
							</span>
						</div>
					}
					open={previewModalOpen}
					onCancel={handlePreviewModalClose}
					footer={
						<div className="flex flex-col sm:flex-row justify-end gap-3">
							<Button onClick={handlePreviewModalClose} className="w-full sm:w-auto">
								Close
							</Button>
							{previewModalData?.type === ENotificationType.CALL && (
								<Button
									type="primary"
									className="bg-blue-600 hover:bg-blue-700 border-blue-600 w-full sm:w-auto"
									onClick={() => handleUpdateCustomization()}
								>
									Save Changes
								</Button>
							)}
						</div>
					}
					width={window.innerWidth < 768 ? '95%' : 600}
					centered
					className="notification-modal"
				>
					{previewModalData && (
						<div className="space-y-6 mt-6">
							{/* Notification Type Info */}
							<div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
								<h3 className="text-lg font-semibold text-gray-900 mb-3">Notification Details</h3>
								<div className="space-y-3">
									<div className="flex justify-between">
										<span className="text-gray-600">Type:</span>
										<Tag color={getNotificationTypeColor(previewModalData.type)}>
											{getNotificationTypeLabel(previewModalData.type)}
										</Tag>
									</div>
									<div className="flex justify-between">
										<span className="text-gray-600">Method:</span>
										<Tag color={getMethodColor(previewModalData?.method)}>
											{getMethodIcon(previewModalData?.method)}{' '}
											{getMethodLabel(previewModalData?.method)}
										</Tag>
									</div>
									<div className="flex justify-between">
										<span className="text-gray-600">Recipient:</span>
										<span className="font-medium">{previewModalData.values.recipient}</span>
									</div>
									{previewModalData.values.customScenario && (
										<div>
											<span className="text-gray-600">Custom Scenario:</span>
											<div className="mt-1 p-3 bg-white rounded border border-gray-200 text-sm">
												"{previewModalData.values.customScenario}"
											</div>
										</div>
									)}
									{previewModalData.values.crmGroupName && (
										<div className="flex justify-between">
											<span className="text-gray-600">CRM Group:</span>
											<span className="font-medium">{previewModalData.values.crmGroupName}</span>
										</div>
									)}
									{previewModalData.values.intake && (
										<div className="flex justify-between">
											<span className="text-gray-600">Intake Workflow Scenario:</span>
											<span className="font-medium">{previewModalData.values.intake}</span>
										</div>
									)}
								</div>
							</div>

							{/* Preview */}
							<div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
								<div className="flex items-center justify-between">
									<h3 className="text-lg font-semibold text-gray-900 mb-3">
										Sample {previewModalData?.method} notification
									</h3>
									<Button
										onClick={() => {
											previewModalData?.method === ENotificationMethod.EMAIL
												? setPreviewEmail(true)
												: setPreviewSms(true);
										}}
									>
										Full Preview
									</Button>
								</div>
								<div className="bg-white p-4 rounded-lg border border-blue-200">
									<div className="text-gray-800 italic">
										"
										{getNotificationPreview(
											previewModalData.type,
											previewModalData?.method,
											previewModalData.values,
										)}
										"
									</div>
								</div>
							</div>

							{/* Customize options for call notifications */}
							{previewModalData.type === ENotificationType.CALL && (
								<div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
									<h3 className="text-lg font-semibold text-gray-900 mb-3">
										Customization Options
									</h3>
									<div className="space-y-3">
										<div className="text-sm text-gray-600 mb-2">
											Choose what information to include in your call notifications:
										</div>
										<div className="grid grid-cols-2 gap-3">
											{previewModalData?.method === ENotificationMethod.EMAIL && (
												<>
													<label className="flex items-center gap-2">
														<input
															type="checkbox"
															className="rounded cursor-pointer"
															checked={customizationSettings.showTranscript}
															onChange={(e) =>
																setCustomizationSettings((prev) => ({
																	...prev,
																	showTranscript: e.target.checked,
																}))
															}
														/>
														<span className="text-sm">Call transcript</span>
													</label>
													<label className="flex items-center gap-2">
														<input
															type="checkbox"
															checked={customizationSettings.showCallSummary}
															onChange={(e) =>
																setCustomizationSettings((prev) => ({
																	...prev,
																	showCallSummary: e.target.checked,
																}))
															}
															className="rounded cursor-pointer"
														/>
														<span className="text-sm">Call summary</span>
													</label>
													<label className="flex items-center gap-2">
														<input
															type="checkbox"
															checked={customizationSettings.showIntakeForms}
															onChange={(e) =>
																setCustomizationSettings((prev) => ({
																	...prev,
																	showIntakeForms: e.target.checked,
																}))
															}
															className="rounded cursor-pointer"
														/>
														<span className="text-sm">Intake forms</span>
													</label>
													<label className="flex items-center gap-2">
														<input
															type="checkbox"
															checked={customizationSettings.showWorkflow}
															onChange={(e) =>
																setCustomizationSettings((prev) => ({
																	...prev,
																	showWorkflow: e.target.checked,
																}))
															}
															className="rounded cursor-pointer"
														/>
														<span className="text-sm">Workflow actions</span>
													</label>
												</>
											)}

											{previewModalData?.method === ENotificationMethod.SMS && (
												<>
													<label className="flex items-center gap-2">
														<input
															type="checkbox"
															checked={customizationSettings.showCallSummaryText}
															onChange={(e) =>
																setCustomizationSettings((prev) => ({
																	...prev,
																	showCallSummaryText: e.target.checked,
																}))
															}
															className="rounded cursor-pointer"
														/>
														<span className="text-sm">Call Summary</span>
													</label>
													<label className="flex items-center gap-2">
														<input
															type="checkbox"
															checked={customizationSettings.showCallInfoLinkText}
															onChange={(e) =>
																setCustomizationSettings((prev) => ({
																	...prev,
																	showCallInfoLinkText: e.target.checked,
																}))
															}
															className="rounded cursor-pointer"
														/>
														<span className="text-sm">Call Info Link</span>
													</label>
												</>
											)}
										</div>
									</div>
								</div>
							)}
						</div>
					)}
				</Modal>

				{/* Add Recipient Modal */}
				<Modal
					title={
						<div className="flex items-center gap-3">
							<div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
								<BellOutlined className="text-green-600" />
							</div>
							<span className="text-lg md:text-xl font-semibold">Add Recipient</span>
						</div>
					}
					open={addRecipientModalOpen}
					onCancel={handleAddRecipientModalClose}
					footer={null}
					width={window.innerWidth < 768 ? '95%' : 500}
					centered
					className="notification-modal"
				>
					<Form
						form={addRecipientForm}
						layout="vertical"
						onFinish={handleAddRecipientSubmit}
						className="mt-6"
					>
						<div className="space-y-4">
							<div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
								<p className="text-blue-800 text-sm mb-3">
									Add another recipient to receive this notification. The new recipient will use the
									same notification method (email or SMS) as the existing recipients.
								</p>
								{selectedNotificationId && (
									<div className="text-xs text-blue-600">
										Current method:{' '}
										<strong>
											{simpleNotifications.find((n) => n.id === selectedNotificationId)?.method ===
											ENotificationMethod.EMAIL
												? ENotificationMethod.EMAIL
												: ENotificationMethod.SMS}
										</strong>
									</div>
								)}
							</div>

							<Form.Item
								name="newRecipient"
								label="New Recipient"
								rules={[
									{ required: true, message: 'Please enter recipient details' },
									{
										validator: async (_, value) => {
											if (!value) {
												return Promise.resolve();
											}

											const selectedNotification = simpleNotifications.find(
												(n) => n.id === selectedNotificationId,
											);
											const method = selectedNotification?.method;

											if (!method) {
												return Promise.reject(new Error('Unable to determine notification method'));
											}

											const entries = value
												.split(',')
												.map((entry: string) => entry.trim())
												.filter(Boolean);

											if (entries.length === 0) {
												return Promise.reject(new Error('Please enter at least one valid entry'));
											}

											if (method === ENotificationMethod.EMAIL) {
												const invalidEmails = entries.filter(
													(email: string) => !isValidEmail(email),
												);
												if (invalidEmails.length > 0) {
													return Promise.reject(
														new Error(`Invalid email(s): ${invalidEmails.join(', ')}`),
													);
												}
											} else if (method === ENotificationMethod.SMS) {
												const invalidPhones: string[] = [];

												for (const phone of entries) {
													if (!phone.startsWith('+')) {
														invalidPhones.push(`${phone} (missing "+")`);
														continue;
													}

													const isValid = isValidPhone(phone);
													if (!isValid) {
														invalidPhones.push(`${phone} (invalid format)`);
													}
												}

												if (invalidPhones.length > 0) {
													return Promise.reject(
														new Error(`Invalid phone number(s): ${invalidPhones.join(', ')}`),
													);
												}
											}

											return Promise.resolve();
										},
									},
								]}
								validateTrigger={['onChange', 'onBlur']}
								dependencies={[selectedNotificationId]}
							>
								<Input
									size="large"
									placeholder={
										selectedNotificationId &&
										simpleNotifications.find((n) => n.id === selectedNotificationId)?.method ===
											ENotificationMethod.EMAIL
											? 'Enter email address(es) - separate multiple with commas'
											: 'Enter phone number(s) with + prefix - separate multiple with commas'
									}
								/>
							</Form.Item>
						</div>

						<div className="flex justify-end gap-3 mt-6 pt-4">
							<Button onClick={handleAddRecipientModalClose}>Cancel</Button>
							<Button
								type="primary"
								htmlType="submit"
								className="relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium py-2 px-4 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl"
							>
								<div className="absolute inset-0 pointer-events-none">
									<div className="absolute top-[15%] left-[15%] w-[50%] h-[50%] bg-white/20 rounded-full blur-xl" />
									<div className="absolute bottom-[15%] right-[15%] w-[50%] h-[50%] bg-white/15 rounded-full blur-xl" />
									<div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[50%] h-[50%] bg-white/10 rounded-full blur-xl" />
								</div>
								<span className="relative z-10">Add Recipient</span>
							</Button>
						</div>
					</Form>
				</Modal>

				<PreviewEmail
					open={previewEmail}
					setOpen={setPreviewEmail}
					disabled={isDisabled}
					notifKey={getPreviewKey(previewModalData?.type)}
					show={PreviewShowObject}
					onShowChange={onPreviewShowChange}
					resellerName={currentNoviReseller?.businessName}
					resellerLogo={currentNoviReseller?.logo}
					resellerClient={loggedInUser?.resellerClient}
				/>
				<PreviewSms
					open={previewSms}
					setOpen={setPreviewSms}
					disabled={isPhoneDisabled}
					notifKey={getPreviewKey(previewModalData?.type)}
					show={PreviewShowObject}
					onShowChange={onPreviewShowChange}
				/>
			</div>
		</Spin>
	);
};
